import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Check, CalendarCheck } from "lucide-react";
import { findService, SERVICES } from "@/data/services";
import Reveal from "@/components/Reveal";
import Seo from "@/components/Seo";
import ServiceExtras from "@/components/ServiceExtras";

const SERVICE_KEYWORDS = {
  "ahu-maintenance": { kw: "AHU maintenance London", meta: "AHU maintenance London — filter changes, motor checks, coil cleaning and full air handling unit servicing across West London. SFG20 and BS EN 16798-1 compliant." },
  "ac-refrigeration": { kw: "F-Gas engineer West London", meta: "Air conditioning, refrigeration and F-Gas compliance across West London. Refcom-certified engineers, log books and leak testing. BS EN 378 / BS ISO 5149." },
  "kitchen-extract": { kw: "TR19 duct cleaning London", meta: "TR19 kitchen extract duct cleaning across London — grease removal, fire-risk reduction, photographic evidence and certification for insurers." },
  "steam-trap-psv": { kw: "Steam trap & PSV maintenance London", meta: "Steam trap and pressure safety valve maintenance across London — energy-loss surveys, ultrasonic testing, PSSR-compliant certification." },
  "mvhr-ventilation": { kw: "MVHR maintenance London", meta: "MVHR maintenance, toilet extract, door curtains and air purifiers across London. Heat exchanger cleaning, ISO 16890 filter changes, Building Regs Part F." },
  "fire-damper-drop-testing": { kw: "Fire damper drop testing London", meta: "Fire damper drop testing across London — BS 9999 and RRFSO compliance, asset registers, remedial works and full certification for fire risk assessors." },
};

const ServiceDetail = ({ onBook }) => {
  const { slug } = useParams();
  const service = findService(slug);
  if (!service) return <Navigate to="/services" replace />;

  const otherServices = SERVICES.filter((s) => s.slug !== slug).slice(0, 3);
  const seo = SERVICE_KEYWORDS[slug] || { meta: service.short };

  // Per-service Schema.org Service node, linked to the LocalBusiness provider
  const serviceJsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `https://calmbreeze.co.uk/services/${slug}#service`,
    name: service.title,
    description: seo.meta || service.short,
    url: `https://calmbreeze.co.uk/services/${slug}`,
    image: service.image,
    serviceType: service.title,
    provider: { "@id": "https://calmbreeze.co.uk/#org" },
    areaServed: [
      { "@type": "City", "name": "London" },
      { "@type": "AdministrativeArea", "name": "West London" },
      { "@type": "AdministrativeArea", "name": "Greater London" },
      { "@type": "AdministrativeArea", "name": "Surrey" },
      { "@type": "AdministrativeArea", "name": "Berkshire" },
    ],
    audience: { "@type": "BusinessAudience", audienceType: "Commercial property operators, facilities managers, landlords" },
    offers: {
      "@type": "Offer",
      priceCurrency: "GBP",
      url: "https://calmbreeze.co.uk/contact",
      availability: "https://schema.org/InStock",
      eligibleRegion: { "@type": "Country", "name": "United Kingdom" },
    },
  };

  return (
    <>
      <Seo
        title={`${service.title} | Calmbreeze`}
        description={seo.meta}
        path={`/services/${slug}`}
        image={service.image}
        jsonLd={serviceJsonLd}
      />
      <section className="pt-[140px] pb-12 max-w-editorial mx-auto px-6 md:px-10 lg:px-12">
        <Link
          to="/services"
          className="inline-flex items-center gap-2 text-[13px] font-mono tracking-wide text-muted-foreground hover:text-ink mb-12"
          data-testid="service-back"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> All services
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="font-mono text-[11px] tracking-overline uppercase text-accent mb-4">
              [ {service.overline} — Service ]
            </div>
            <h1 className="font-display font-bold text-[2.5rem] md:text-[4.5rem] leading-[0.98] tracking-tightest text-ink">
              {service.title}.
            </h1>
            <p className="mt-8 text-[18px] md:text-[20px] text-muted-foreground max-w-2xl leading-relaxed">
              {service.intro}
            </p>
          </div>
          <div className="lg:col-span-4 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={onBook}
              data-testid={`service-cta-book-${service.slug}`}
              className="inline-flex items-center gap-2 px-6 py-3.5 bg-accent text-white font-semibold text-[14px] rounded-sm hover:bg-brand transition-colors"
            >
              Book a free survey <CalendarCheck className="h-4 w-4" />
            </button>
            <Link
              to="/contact"
              data-testid={`service-cta-${service.slug}`}
              className="inline-flex items-center gap-2 px-6 py-3.5 border border-ink text-ink font-semibold text-[14px] rounded-sm hover:bg-ink hover:text-white transition-colors"
            >
              Discuss your site <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="py-12 max-w-editorial mx-auto px-6 md:px-10 lg:px-12">
        <div className="aspect-[21/9] rounded-md overflow-hidden">
          <img src={service.image} alt={service.title} className="w-full h-full object-cover" />
        </div>
      </section>

      <section className="py-24 max-w-editorial mx-auto px-6 md:px-10 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <div className="prose-editorial">
              {service.body.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
              <h2>{service.coverageHeading}</h2>
              <p>{service.deepDive}</p>
            </div>
          </div>
          <div className="lg:col-span-5 lg:pl-8">
            <Reveal className="bg-paper-warm border border-black/8 rounded-md p-8 lg:sticky lg:top-28">
              <div className="font-mono text-[11px] tracking-overline uppercase text-muted mb-4">
                [ Scope ]
              </div>
              <h3 className="font-display font-semibold text-[1.5rem] tracking-tight mb-6">
                What’s included
              </h3>
              <ul className="space-y-4">
                {service.coverage.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Check className="h-4 w-4 text-brand mt-1 shrink-0" strokeWidth={2.4} />
                    <span className="text-[14px] text-ink/80 leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/contact"
                className="mt-8 inline-flex items-center gap-2 text-[14px] font-semibold border-b border-ink/30 pb-1 hover:border-ink transition-colors"
                data-testid={`service-side-cta-${service.slug}`}
              >
                Request a survey <ArrowUpRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      <ServiceExtras service={service} onBook={onBook} />

      <section className="py-24 bg-paper-warm">
        <div className="max-w-editorial mx-auto px-6 md:px-10 lg:px-12">
          <div className="font-mono text-[11px] tracking-overline uppercase text-muted mb-4">[ More services ]</div>
          <h2 className="font-display font-bold text-[2rem] md:text-[2.5rem] tracking-tightest mb-12">
            Continue exploring
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {otherServices.map((s) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                data-testid={`related-service-${s.slug}`}
                className="group block bg-white border border-black/5 rounded-md p-8 hover:-translate-y-1 transition-all duration-300"
              >
                <div className="font-mono text-[11px] tracking-overline uppercase text-muted mb-6">
                  {s.overline}
                </div>
                <h3 className="font-display font-semibold text-[1.25rem] tracking-tight mb-3 group-hover:text-brand transition-colors">
                  {s.title}
                </h3>
                <p className="text-[13px] text-muted-foreground leading-relaxed">{s.short}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default ServiceDetail;
